import { Box, Button, Card, CardContent, Grid, CardHeader, Typography, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { apiConstants, getGeneric, postGeneric } from '../../common/constants'
import * as React from 'react'
import PageTitle from "../../components/PageTitle";
import { useSnackbar } from "notistack";
import Loader from "../../common/Loader";
import { useForm } from "react-hook-form";
import { FormProvider, RHFSelectField } from "../../common/hook-form";
import { LoadingButton } from "@mui/lab";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";

function VerificationForm({ type, title }) {
    const [loading, setLoading] = useState(false)
    const [configLoading, setConfigLoading] = useState(false)
    const [configList, setConfigList] = useState([])
    const { enqueueSnackbar } = useSnackbar();
    const methods = useForm({
        defaultValues: {
            merchant: '',
            extChannels: '',
            status: '',
            paymentMode: '',
        }
    });

    const { handleSubmit, reset, setValue, watch } = methods;
    const merchant = watch('merchant');
    const extChannels = watch('extChannels');
    const listKey = type === 'order' ? 'orderConfirmation' : 'addressVerification';

    // Load whatever is already saved for this merchant so the form shows the
    // current stored config instead of starting blank.
    const loadConfig = React.useCallback(async (merchantId) => {
        if (!merchantId) { setConfigList([]); return; }
        setConfigLoading(true);
        try {
            const res = await getGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}`);
            const list = res?.data?.channelOrderExternalAction?.[listKey];
            const arr = Array.isArray(list) ? list : [];
            setConfigList(arr);
            // Auto-select the first configured channel so all three fields populate on
            // merchant select. The channel effect below fills Status + Payment Mode for it.
            setValue('extChannels', arr.length > 0 ? (arr[0].extChannels || '') : '');
            if (arr.length === 0) {
                setValue('status', '');
                setValue('paymentMode', '');
            }
        } catch (err) {
            setConfigList([]);
        } finally {
            setConfigLoading(false);
        }
    }, [listKey, setValue]);

    // On merchant change: fetch existing config and auto-populate the fields.
    useEffect(() => {
        loadConfig(merchant);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchant]);

    // On channel change: fill Status + Payment Mode from that channel's saved entry,
    // or clear them if that channel isn't configured.
    useEffect(() => {
        if (!merchant || !extChannels) return;
        const entry = configList.find((c) => c?.extChannels === extChannels);
        setValue('status', entry ? entry.status : '');
        setValue('paymentMode', entry ? (entry.paymentMode || '') : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extChannels, configList]);

    const onSubmit = (data) => {
        setLoading(true)
        const payload = {
            extChannels: data.extChannels,
            status: data.status,
            paymentMode: data.paymentMode,
        }
        const endpoint = type === 'order'
            ? apiConstants.CHANNEL_ORDER_VERIFICATION
            : apiConstants.CHANNEL_ADDRESS_VERIFICATION;

        postGeneric(`${endpoint}/${data.merchant}`, payload).then((res) => {
            enqueueSnackbar("Success", { variant: 'success' });
            // Refresh the saved config so the fields reflect the value just saved.
            loadConfig(data.merchant);
            setLoading(false)
        }).catch((err) => {
            setLoading(false)
            enqueueSnackbar("Some error occurred", { variant: 'error' });
            console.log(err);
        })
    };

    const channelOptions = [
        { value: 'WOO_COMMERCE', label: 'WOO_COMMERCE' },
        { value: 'SHOPIFY', label: 'SHOPIFY' },
        { value: 'MAGENTO', label: 'MAGENTO' },
    ]

    const statusOptions = [
        { value: true, label: 'True' },
        { value: false, label: 'False' },
    ]

    const paymentModeOptions = [
        { value: 'COD', label: 'COD' },
        { value: 'BOTH', label: 'BOTH' },
        { value: 'PREPAID', label: 'PREPAID' },
    ]

    return (
        <Card sx={{ mb: 2 }}>
            {loading && <Loader />}
            <CardHeader title={title} />
            <CardContent>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={4} sx={{ overflow: 'hidden' }}>
                            <MerchantFilterCached name="merchant" label="Select Merchant" />
                        </Grid>
                        <Grid item xs={4} sx={{ overflow: 'hidden' }}>
                            <RHFSelectField
                                width="100%"
                                name="extChannels"
                                label={`Select Channel`}
                                options={channelOptions}
                            />
                        </Grid>
                        <Grid item xs={4} sx={{ overflow: 'hidden' }}>
                            <RHFSelectField
                                width="100%"
                                name="status"
                                label={`Status`}
                                options={statusOptions}
                            />
                        </Grid>
                        <Grid item xs={4} sx={{ overflow: 'hidden' }}>
                            <RHFSelectField
                                width="100%"
                                name="paymentMode"
                                label={`Payment Mode`}
                                options={paymentModeOptions}
                            />
                        </Grid>
                        {merchant && configLoading && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                        Loading saved configuration…
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        {merchant && !configLoading && configList.length === 0 && (
                            <Grid item xs={12}>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'warning.main' }}>
                                    No values are configured for this merchant yet. Select a channel and set the values to configure it.
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={8} sx={{ overflow: 'hidden' }}>
                            <LoadingButton
                                variant="contained"
                                sx={{ ml: 1 }}
                                size="small"
                                type="submit"
                            >
                                Submit
                            </LoadingButton>
                            <Button
                                sx={{ ml: 1 }}
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => { reset(); }}
                            >
                                Reset
                            </Button>
                        </Grid>
                    </Grid>
                </FormProvider>
            </CardContent>
        </Card>
    );
}

function ChannelConfigPage() {
    return (
        <div>
            <PageTitle>Channel Configurations</PageTitle>
            <Grid sx={{ paddingBottom: 2 }}>
                <VerificationForm
                    type="order"
                    title="Channel Order Verification"
                />
                <VerificationForm
                    type="address"
                    title="Channel Address Verification"
                />
            </Grid>
        </div>
    );
}

export default ChannelConfigPage;
